// Client-side image compression for uploads.
//
// Resizes large images to at most MAX_DIMENSION on the longest edge and
// re-encodes them at QUALITY. Skips compression for already-small files,
// non-image inputs, and formats the canvas can't decode reliably (SVG, HEIC).
// PNG input is re-emitted as PNG to preserve transparency (logos), everything
// else becomes JPEG.

const MAX_DIMENSION = 1600;
const QUALITY = 0.85;
const SKIP_BELOW_BYTES = 300 * 1024; // already small enough — leave alone

const PASSTHROUGH_MIME = new Set([
  "image/svg+xml",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

export async function compressImage(file) {
  if (!file || !(file instanceof Blob)) return file;
  const mime = file.type || "";
  if (!mime.startsWith("image/")) return file;
  if (PASSTHROUGH_MIME.has(mime)) return file;
  if (file.size < SKIP_BELOW_BYTES) return file;

  try {
    const img = await loadImage(file);
    const { width: w, height: h } = img;
    if (!w || !h) return file;

    const longest = Math.max(w, h);
    const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const isPng = mime === "image/png";
    const outType = isPng ? "image/png" : "image/jpeg";
    const outQuality = isPng ? undefined : QUALITY;

    const compressed = await canvasToBlob(canvas, outType, outQuality);
    if (!compressed || compressed.size >= file.size) return file;

    // Preserve filename so the server-side handler keeps its existing extension logic.
    const originalName = file.name || "image";
    const newExt = isPng ? "png" : "jpg";
    const dotIdx = originalName.lastIndexOf(".");
    const baseName = dotIdx > 0 ? originalName.slice(0, dotIdx) : originalName;
    return new File([compressed], `${baseName}.${newExt}`, {
      type: outType,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

// Walks a FormData, compresses any image entries in place, returns a new FormData.
export async function compressFormDataImages(formData) {
  const out = new FormData();
  for (const [key, value] of formData.entries()) {
    if (value instanceof Blob && (value.type || "").startsWith("image/")) {
      const compressed = await compressImage(value);
      const filename = value.name || (key === "image" ? "image" : key);
      out.append(key, compressed, filename);
    } else {
      out.append(key, value);
    }
  }
  return out;
}
